import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { ContentBlock } from '../../pages/components/';
import BaseFormContainer from '../../common/components/form/BaseFormContainer';

import * as channelActions from '../../channel/channel.actions';
import * as regionActions from '../../region/region.actions';
import * as userActions from '../user.actions';
import * as teamActions from '../../team/team.actions';
import { UserRoles } from '../user.constants';
import { getAllChannelsWithAdminUser } from '../../channel/channel.selectors';
import { getAllRegionsWithAdminUser } from '../../region/region.selectors';
import { getAllTeamsWithAdminUser } from '../../team/team.selectors';
import { getCurrentUser } from '../user.selectors';
import CreateUserForm from './CreateUserForm';
import { StyledFormFooter } from '../../common/components/form/ui';
import Button from '../../../ui/button/Button';
import colorThemes from '../../../ui/colorThemes';
import { Localize } from '../../../locale/localization';

const constraints = {
  email: {
    email: {
      message: `^${Localize('form.create-user.email.error')}`
    }
  },
  team: {
    presence: {
      message: `^${Localize('form.create-user.team.error')}`
    },
  },
};

const baseFormData = {
  email: '',
  channel: '',
  region: '',
  team: '',
};

class CreateUserFormContainer extends BaseFormContainer {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      formData: Object.assign({}, baseFormData),
    };
  }

  componentWillMount = () => {
    const {
      getTeams,
      getChannels,
      getRegions,
      getRegionsByChannel,
      currentUser,
    } = this.props;

    if(currentUser.role === UserRoles.SUPERUSER) {
      getChannels();
      getRegions();
      getTeams();
    } else if(currentUser.role === UserRoles.SALESMANAGER) {
      getRegionsByChannel(currentUser.channel._id);
      getTeams();
      const { channel } = currentUser;
      const formData = this.state.formData;
      this.setState({
        formData: {
          ...formData,
          channel: channel._id,
        },
      });
    } else if(currentUser.role === UserRoles.REGIONMANAGER) {
      getTeams();
      const { channel, region } = currentUser;
      const formData = this.state.formData;
      this.setState({
        formData: {
          ...formData,
          channel: channel._id,
          region: region && region._id ? region._id : '',
        },
      });
    } else {
      const { team, channel } = currentUser;
      const formData = this.state.formData;
      this.setState({
        formData: {
          ...formData,
          channel: channel._id,
          region: team && team.region ? team.region : '',
          team: team._id,
        },
      });
    }
  }

  getConstraints = () => constraints;

  doSubmit = (formData) => {
    const { teams, userRole } = this.props;
    const selectedTeam = teams.filter(t => t._id === formData.team);
    const channel = selectedTeam && selectedTeam.length > 0 ? selectedTeam[0].channel : null;
    const region = selectedTeam && selectedTeam.length > 0 ? selectedTeam[0].region : null;
    const modifiedFormData = {
      ...formData,
      role: userRole,
      channel: channel || this.state.formData.channel,
      region: region || this.state.formData.region,
    };

    const { createUser } = this.props;
    createUser(modifiedFormData).then((action) => {
      if(action && action.response && action.response.status === 'ok') {
        this.setState({
          isSubmitting: false,
          submitCompleted: true,
          submitted: true,
          formError: '',
        });
      } else {
        this.setState({
          isSubmitting: false,
          submitFailed: true,
          submitted: true,
          formError: action && action.error && `${Localize('form.create-user.error')}`,
        });
      }
    });
  }

  onRetry = (event) => {
    event.preventDefault();
    
    this.setState({
      submitted: false,
      submitFailed: false,
    }, () => {
      this.doSubmit(this.state.formData);
    });
  }

  onReset = (event) => {
    event.preventDefault();

    this.reset();
    this.setState({
      formData: Object.assign({}, baseFormData),
    });
  }

  onCancel = (event) => {
    event.preventDefault();
    
    const { cancelPath, history } = this.props;
    history.replace(cancelPath);
  }

  render() {
    const { channels, regions, teams, userRole } = this.props;
    const title = userRole === UserRoles.SELLER ? 'admin.add.sales.person' : 'admin.add.assistant';

    return (
      <ContentBlock title={Localize(title)}>
        {!this.state.submitted || this.state.formError ? (
          <div>
            <p>{Localize('admin.user.invite')}</p>
            <CreateUserForm
              enabled={!this.state.isSubmitting}
              onCancel={this.onCancel}
              onSubmit={this.onSubmit}
              onInputChange={this.onInputChange}
              onBlur={this.onBlur}
              errors={this.state.errors}
              touchedFields={this.state.touchedFields}
              isValid={this.state.isValid}
              email={this.state.formData.email}
              channel={this.state.formData.channel}
              channels={channels}
              region={this.state.formData.region}
              regions={regions}
              team={this.state.formData.team}
              teams={teams}
              formError={this.state.formError}
            />
          </div>
        ) : (
          this.state.submitCompleted ? (
            <div>
              <p>{Localize('admin.team.user')}</p>
              <StyledFormFooter column>
                <Button
                  theme={colorThemes.LIGHT}
                  onClick={this.onReset}
                  icon="#plus"
                >
                  {Localize('admin.add.user')}
                </Button>
                <Button theme={colorThemes.LIGHT} onClick={this.onCancel}>{Localize('admin.users.created.list')}</Button>
              </StyledFormFooter>
            </div>
          ) : (
            <div>
              <p>{Localize('error.general')}</p>
              <StyledFormFooter column>
                <Button theme={colorThemes.LIGHT} onClick={this.onRetry}>{Localize('error.retry')}</Button>
                <Button theme={colorThemes.LIGHT} onClick={this.onCancel}>{Localize('admin.users.created.list')}</Button>
              </StyledFormFooter>
            </div>
          )
        )}
      </ContentBlock>
    );
  }
}

const mapStateToProps = (state, { cancelPath, userRole = UserRoles.SELLER }) => {
  const currentUser = getCurrentUser(state);

  const channels = currentUser.role === UserRoles.SUPERUSER ? getAllChannelsWithAdminUser(state) : [];
  const regions = currentUser.role === UserRoles.SUPERUSER || currentUser.role === UserRoles.SALESMANAGER ? getAllRegionsWithAdminUser(state) : [];
  const teams = currentUser.role !== UserRoles.TEAMLEADER ? getAllTeamsWithAdminUser(state) : [];
  
  return {
    channels,
    regions,
    teams,
    currentUser,
    cancelPath,
    userRole,
  };
};

const ConnectedCreateUserFormContainer = connect(
  mapStateToProps,
  Object.assign({}, userActions, teamActions, channelActions, regionActions),
)(CreateUserFormContainer);

export default withRouter(ConnectedCreateUserFormContainer);


// WEBPACK FOOTER //
// ./src/modules/user/components/CreateUserFormContainer.js